import React from "react";
import { Info, Sparkles, GitFork, ArrowRight, Lightbulb } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { LifeRecord } from "@/lib/infiniteSelves";
export interface EarthComparisonTabProps {
  life: LifeRecord | null;
  cardHue?: number;
}
export default function EarthComparisonTab({
  life,
  cardHue = 186
}: EarthComparisonTabProps): JSX.Element {
  if (!life) {
    return <div className="space-y-6">
        <div className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border text-xs text-muted-foreground font-sans">
          <Info className="size-4 text-primary shrink-0" />
          <span>This chapter is not available for this result. Generate again to see it.</span>
        </div>
        <div className="p-4 sm:p-5 rounded-xl bg-card/65 border border-border/70 space-y-2">
          <span className="text-xs font-mono uppercase tracking-widest text-primary font-bold block">
            What This Comparison Covers
          </span>
          <p className="text-xs text-muted-foreground leading-relaxed font-sans">
            Compares this persona across eight canonical life dimensions against a general modern Earth reference, mapping specific divergence catalysts and revealing which of your questionnaire answers anchored each aspect of this reality.
          </p>
        </div>
      </div>;
  }
  const {
    earthComparison
  } = life;
  if (!earthComparison) return <></>;
  return <div className="space-y-6">
      {/* Prominent Basis Notice Banner */}
      <div className="p-4 sm:p-5 rounded-xl border border-primary/40 bg-primary/10 shadow-sm space-y-1.5 relative overflow-hidden">
        <div className="flex items-center gap-2">
          <Info className="size-4 text-primary shrink-0" aria-hidden="true" />
          <span className="font-mono text-xs font-bold uppercase tracking-wider text-primary">
            General Reference Standard · Not Facts About You
          </span>
        </div>
        <p className="text-xs text-foreground/90 font-sans leading-relaxed">
          {earthComparison.basisNote || "The Earth column is a general reference for a typical modern Earth life, not personal facts about you, because the questionnaire holds no real-world identifying information."}
        </p>
      </div>

      {/* 8 Dimensional Comparison Rows */}
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b border-border/60 pb-2">
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5">
            <Sparkles className="size-3.5 text-primary shrink-0" />
            <span>8 Dimensional Life Comparisons</span>
          </span>
          <span className="text-[10px] font-mono text-primary uppercase">
            Multiverse Life Record
          </span>
        </div>

        <div className="space-y-3">
          {earthComparison.rows?.map((row, idx) => <div key={idx} className="p-4 sm:p-5 rounded-xl bg-card border border-border shadow-sm space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/40 pb-2">
                <span className="font-serif font-bold text-sm sm:text-base text-foreground flex items-center gap-2">
                  <span className="font-mono text-xs text-primary font-bold">
                    0{idx + 1}.
                  </span>
                  <span>{row.dimension}</span>
                </span>
                {row.divergenceId && <Badge variant="outline" className="text-[10px] font-mono uppercase border-chart-3/40 text-chart-3 bg-chart-3/10">
                    Divergence #{row.divergenceId}
                  </Badge>}
              </div>

              {/* Responsive 2-column on desktop, stacked on mobile */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs sm:text-sm">
                <div className="p-3 rounded-lg bg-primary/5 border border-primary/25 space-y-1">
                  <span className="text-[10px] font-mono uppercase text-primary font-bold block">
                    In This Universe
                  </span>
                  <p className="text-foreground/95 font-sans leading-relaxed">
                    {row.here}
                  </p>
                </div>

                <div className="p-3 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                  <span className="text-[10px] font-mono uppercase text-muted-foreground font-bold block">
                    Earth (General Reference)
                  </span>
                  <p className="text-muted-foreground font-sans leading-relaxed">
                    {row.earth}
                  </p>
                </div>
              </div>

              {/* Because line */}
              {row.because && <div className="flex items-start gap-2 pt-1 text-xs text-muted-foreground font-sans">
                  <ArrowRight className="size-3.5 text-primary shrink-0 mt-0.5" />
                  <p className="leading-relaxed">
                    <strong className="text-foreground/90 font-medium">Why it diverged: </strong>
                    {row.because}
                  </p>
                </div>}
            </div>)}
        </div>
      </div>

      {/* Divergence Cards with Effects */}
      {earthComparison.divergences?.length > 0 && <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between border-b border-border/60 pb-2">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5">
              <GitFork className="size-3.5 text-chart-3 shrink-0" />
              <span>Divergence Catalysts ({earthComparison.divergences.length})</span>
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {earthComparison.divergences.map(div => <div key={div.id} className="p-4 rounded-xl bg-card border border-border space-y-2">
                <div className="flex items-baseline justify-between gap-2 border-b border-border/40 pb-1.5">
                  <span className="font-serif font-bold text-sm text-foreground">
                    {div.title}
                  </span>
                  <Badge variant="outline" className="text-[10px] font-mono text-primary border-primary/40">
                    {div.id}
                  </Badge>
                </div>
                <p className="text-xs text-foreground/90 leading-relaxed font-sans">
                  {div.text}
                </p>
                {div.effects && <div className="border-t border-border/40 pt-2 text-[11px] text-muted-foreground">
                    <span className="font-mono uppercase text-primary font-bold mr-1">
                      Effects:
                    </span>
                    <span>{div.effects}</span>
                  </div>}
              </div>)}
          </div>
        </div>}

      {/* Visitor Signals: How your answers shaped this */}
      {earthComparison.visitorSignals?.length > 0 && <div className="p-4 sm:p-5 rounded-xl bg-card/80 border border-border space-y-3 pt-4">
          <span className="text-xs font-mono uppercase tracking-wider text-primary font-bold flex items-center gap-2">
            <Lightbulb className="size-3.5 text-primary shrink-0" />
            <span>How Your Answers Shaped This Reality</span>
          </span>
          <ul className="space-y-2 text-xs text-foreground/90 font-sans">
            {earthComparison.visitorSignals.map((signal, idx) => <li key={idx} className="flex items-start gap-2.5 p-2.5 rounded-lg bg-muted/30 border border-border/40">
                <span className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                <span className="leading-relaxed">{signal}</span>
              </li>)}
          </ul>
        </div>}
    </div>;
}